import { useState, type FormEvent } from "react";
import { requestMagicLink } from "../api/client";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      await requestMagicLink(email.trim());
      setSent(true);
    } catch {
      // Deliberately generic: a per-address error here would let anyone probe
      // which email addresses have accounts.
      setError("We couldn't send that link. Please check the address and try again.");
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <main
        className="flex min-h-screen items-center justify-center px-4"
        data-testid="login-sent"
      >
        <div className="w-full max-w-sm rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm dark:border-[#2e2e2e] dark:bg-[#1a1a1a]">
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Check your email</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            If an account exists for <span className="font-medium">{email}</span>, a sign-in link
            is on its way. It expires shortly and can only be used once.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center px-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-[#2e2e2e] dark:bg-[#1a1a1a]"
        data-testid="login-form"
      >
        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Sign in</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          We'll email you a link — no password needed.
        </p>

        <label htmlFor="email" className="mt-5 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Email
        </label>
        <input
          id="email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-emerald-600 focus:outline-none dark:border-[#2e2e2e] dark:bg-[#111111] dark:text-gray-100"
          data-testid="login-input-email"
        />

        {error && (
          <p className="mt-3 text-sm text-amber-600" role="alert" data-testid="login-error">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy}
          className="mt-5 w-full rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
          data-testid="login-button-submit"
        >
          {busy ? "Sending…" : "Send sign-in link"}
        </button>
      </form>
    </main>
  );
}
